import React, { useCallback, useEffect } from 'react';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import RestConfigure from 'js/components/destination/destination-content/RestConfigure';
import usePaging from 'new/state/paging/repository/usePaging';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useFlows from 'new/components/flows/useFlows';
import { CONNECTION_CATEGORY_REST } from 'js/constants/connectionCategories';
import useApp from 'new/state/app/repository/useApp';
import { useSamples } from 'new/state/samples/repository/useSamples';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import useRequests from 'new/state/request/repository/useRequests';
import useFlowParams from '../../useFlowParams';
import { Typography } from '@mui/material';
import { Stack } from 'new/elements/Stack';

type Props = {
  formType: string;
  flowType: string;
  sink: any;
  nexset: any;
  sinkConfig: any;
  isEditMode: boolean;
  selectedConnection?: string;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): void;
  toggleDestinationDraft(value: boolean): void;
};

export const RestAPIDestination = (props: Props) => {
  const { formType, flowType, sink, nexset, sinkConfig, isEditMode, selectedConnection } = props;

  const ctx = useApp();
  const { showError } = useRequests();
  const history = useHistory();
  const { paging } = usePaging();
  const { samples } = useSamples();
  const currentOrg = useCurrentOrg();
  const { getNexsetSchemaById } = useSchemas();

  const { data: formData, updateFormData, onSinkConfigChanged } = useFlowsSetup(formType, flowType);
  const { isModal, onDismiss } = useFlowParams();

  const { onSinkMappingUpdated } = useFlows(updateFormData);
  const nexsetSchema = getNexsetSchemaById(nexset?.id);

  useEffect(() => {
    if (nexset?.id && !formData?.toNested) {
      updateFormData({
        /**
         * Set the nested samples to generate the previewSamples array
         */
        toNested: get(samples, `[${nexset?.id}].original.out`),
      });
    }
  }, [samples, formData?.toNested]);

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  if (!sinkConfig) {
    return (
      <Stack padding="50px" alignItems="center" gap="12px">
        <Typography variant="h6">Sorry, something went wrong.</Typography>
        <Typography variant="body1">Please contact support if the problem persists.</Typography>
      </Stack>
    );
  }

  return (
    <RestConfigure
      formType={formType}
      paging={paging}
      dataSet={nexset}
      addedDataSink={sink}
      currentOrg={currentOrg}
      sinkConfig={sinkConfig}
      isEditMode={isEditMode}
      dataSchema={nexsetSchema}
      selectedConnection={selectedConnection}
      onSinkConfigChanged={onSinkConfigChanged}
      selectedCredentialId={formData?.credentialId}
      onSinkMappingUpdated={onSinkMappingUpdated({
        isEditMode,
        sinkConfig,
        connectionCategory: CONNECTION_CATEGORY_REST,
      })}
      onToggleDestinationDraft={props.toggleDestinationDraft}
      setContextualSupport={ctx.setContextualSupport}
      unsetContextualSupport={ctx.unsetContextualSupport}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={goBack}
      onError={showError}
    />
  );
};
